import type {
  SystemDomainStatus,
  SystemDomainType,
} from '../../constants/system_domains';

export type SearchSection =
  | 'search_filters_panel'
  | 'search_asset_metadata_panel'
  | 'search_collections_panel'
  | 'search_saved_searches_panel'
  | 'search_history_panel'
  | 'collection_filters_panel'
  | 'collection_metadata_panel'
  | 'collection_shares_panel'
  | 'collection_acls_panel'
  | 'saved_search_filters_panel'
  | 'posters';

const SEARCH_SECTIONS = [
  'search_filters_panel',
  'search_asset_metadata_panel',
  'search_collections_panel',
  'search_saved_searches_panel',
  'search_history_panel',
  'collection_filters_panel',
  'collection_metadata_panel',
  'collection_shares_panel',
  'collection_acls_panel',
  'saved_search_filters_panel',
  'posters',
] as const satisfies readonly SearchSection[];

const searchSectionSet: ReadonlySet<string> = new Set(SEARCH_SECTIONS);

export const isSearchSection = (value: string): value is SearchSection =>
  searchSectionSet.has(value);

export type AssetSection =
  | 'transcoders'
  | 'acls'
  | 'comments'
  | 'reviewandapprove'
  | 'time_based_metadata'
  | 'shares'
  | 'history'
  | 'asset_metadata'
  | 'subclips'
  | 'relations'
  | 'transcriptions'
  | 'face_recognition'
  | 'posters'
  | 'publish';

const ASSET_SECTIONS = [
  'transcoders',
  'acls',
  'comments',
  'reviewandapprove',
  'time_based_metadata',
  'shares',
  'history',
  'asset_metadata',
  'subclips',
  'relations',
  'transcriptions',
  'face_recognition',
  'posters',
  'publish',
] as const satisfies readonly AssetSection[];

const assetSectionSet: ReadonlySet<string> = new Set(ASSET_SECTIONS);

export const isAssetSection = (value: string): value is AssetSection =>
  assetSectionSet.has(value);

export type SessionAjaxConfig = {
  canRefresh?: boolean;
  requestHeaders?: {
    'App-ID'?: string;
    'Share-Auth-Token'?: string;
  };
};

export type AppAppState = {
  host: string;
  logo: string;
  iconikLogo: string;
  dateFormat: string;
  dateTimeFormat: string;
  assetDefaultSections: AssetSection[];
  searchDefaultSections: SearchSection[];
  sessionAjaxConfig: SessionAjaxConfig;
  // Absent until the first status lands; a fresh browser has nothing stored.
  systemDomainStatus?: SystemDomainStatus;
  systemDomainWarningMessage: string;
  systemDomainInvoiceEndOfMonth?: boolean;
  differentSystemDomain?: {
    id: string;
    name: string;
  };
  differentUser?: {
    id: string;
    name: string;
  };
  systemDomainBalance: number;
  systemDomainType?: SystemDomainType;
  systemStatusLoaded: boolean;
  appStateKey?: string;
  stripeConfigured?: boolean;
};

export type DifferentSystemDomain = {
  id: string;
  name: string;
  base_url?: string;
};

export type DifferentUser = { id: string; name: string };
